import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/athletesApi';
import IntroAnimation from '../Components/intro/IntroAnimation';
import AthleteGrid from '../Components/athletes/AthleteGrid';
import TruncatedIcosahedron from '../Components/intro/TruncatedIcosahedron';
import { useLanguage } from '../src/context/LanguageContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../Components/ui/dropdown-menu";

export default function Home() {
  const { t, setLanguage, language } = useLanguage();
  const [showIntro, setShowIntro] = useState(true);
  const [hasSeenIntro, setHasSeenIntro] = useState(false);

  useEffect(() => {
    const seen = sessionStorage.getItem('hasSeenIntro');
    if (seen) {
      setShowIntro(false);
      setHasSeenIntro(true);
    }
  }, []);

  const handleIntroComplete = () => {
    setShowIntro(false);
    setHasSeenIntro(true);
    sessionStorage.setItem('hasSeenIntro', 'true');
  };

  const { data: athletes = [], isLoading } = useQuery({
    queryKey: ['athletes', language],
    queryFn: () => base44.entities.Athlete.list(language),
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a1628] via-[#0f2847] to-[#0a1628]">
      <img src="/resources/ZianoDiNotte.webp" style={{ position: 'fixed', top: 0, left: 0, height: '100vh', width: '100vw', objectFit: 'cover', opacity: 0.2 }} />
      <div className="fixed top-4 right-4 z-50">
        <DropdownMenu>
          <DropdownMenuTrigger className="p-2 rounded-full hover:bg-white/10 transition-colors outline-none">
            <img 
              src="/resources/icons8-world-50.png" 
              alt="Switch Language" 
              className="w-8 h-8 opacity-80 hover:opacity-100 transition-opacity"
            />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-[#0f2847] border-blue-500/30 text-white">
            <DropdownMenuItem 
              onClick={() => setLanguage('it')}
              className={`cursor-pointer hover:bg-white/10 focus:bg-white/10 ${language === 'it' ? 'text-blue-400 font-bold' : ''}`}
            >
              Italiano
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => setLanguage('en')}
              className={`cursor-pointer hover:bg-white/10 focus:bg-white/10 ${language === 'en' ? 'text-blue-400 font-bold' : ''}`}
            >
              English
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => setLanguage('de')}
              className={`cursor-pointer hover:bg-white/10 focus:bg-white/10 ${language === 'de' ? 'text-blue-400 font-bold' : ''}`}
            >
              Deutsch
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Intro Animation */}
      {showIntro && <IntroAnimation onComplete={handleIntroComplete} />}

      {/* Main Content */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: hasSeenIntro || !showIntro ? 1 : 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="relative"
      >
        {/* Background Truncated Icosahedron */}
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] opacity-50 pointer-events-none">
          <TruncatedIcosahedron visible={!showIntro} />
        </div>

        {/* Header */}
        <header className="relative z-10 pt-16 pb-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <h1 className="text-Title tracking-[0.4em] mb-2 text-white/90 text-[1.75rem] md:text-[2.5rem] font-thin">
              ZIANO DI FIEMME
            </h1>
            <div className="flex items-center justify-center gap-6 mb-4">
              <div className="w-12 h-[1px] bg-gradient-to-r from-transparent to-blue-500/50" />
              <img 
                src="/resources/Ziano di Fiemme - Stemma HQ96.png" 
                alt="Stemma Ziano di Fiemme" 
                className="h-24 w-auto opacity-80"
              />
              <div className="w-12 h-[1px] bg-gradient-to-l from-transparent to-blue-500/50" />
            </div>
            <h2 className="text-xl md:text-2xl font-extralight text-white/60 tracking-wider mt-6">
              {t('home.subtitle')}
            </h2>
          </motion.div>
        </header>

        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-2 h-2 rounded-full bg-blue-400 opacity-30 animate-pulse" />
          <div className="absolute top-40 right-20 w-3 h-3 rounded-full bg-[#d4af37] opacity-20 animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute bottom-40 left-1/4 w-2 h-2 rounded-full bg-blue-300 opacity-25 animate-pulse" style={{ animationDelay: '2s' }} />
        </div>

        {/* Athletes Grid */}
        <main className="relative z-10 max-w-7xl mx-auto px-4 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="text-center mb-8"
          >
            <p className="text-blue-200/70 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
              {t('home.description')} {t('home.instruction')}
            </p>
          </motion.div>

          <AthleteGrid athletes={athletes} isLoading={isLoading} />

          <p className="text-blue-200/70 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            {t('home.endphrase')}
            <br /><br />
            {t('home.endphrase2')}
          </p>
        </main>       

        {/* Footer */}
        <footer className="relative z-10 py-12 text-center">
          <div className="w-24 h-px bg-gradient-to-r from-transparent via-blue-400/50 to-transparent mx-auto mb-4" />
          <p className="text-blue-300/50 text-xs tracking-widest uppercase">
            Comune di Ziano di Fiemme
          </p>
        </footer>
      </motion.div>
    </div>
  );
}